import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import AppointmentDetailPanel from '../components/AppointmentDetailPanel';
import { useFormattedDateTime } from '../context/TimezoneContext';
import { usePractice } from '../context/PracticeContext';
import TagBadge from '../components/TagBadge';
import TagFilterDropdown from '../components/TagFilterDropdown';
import { useTableSort, sortArrow } from '../hooks/useTableSort';

// Derive the bell color from the push-summary counts the backend now ships on
// each appointment row. Single source of truth so the legend in tooltips
// matches the rendered color.
//   gray   — no intake form completed yet (push_total = 0)
//   green  — all pushes succeeded (pass = total)
//   yellow — Phase 1 done, Phase 2 awaiting check-in (pre-check-in skips)
//   red    — at least one failure
function bellState(appt) {
  if (!appt) return { color: '#cbd5e1', label: 'No form' };
  const fail = appt.push_fail || 0;
  const skipPreCheckin = appt.push_skip_pre_checkin || 0;
  const pass = appt.push_pass || 0;
  const total = appt.push_total || 0;
  if (total === 0) return { color: '#cbd5e1', label: 'No paperwork pushed yet' };
  if (fail > 0) return { color: '#dc2626', label: `${fail} EHR push${fail === 1 ? '' : 'es'} failed — click to review` };
  if (skipPreCheckin > 0) return { color: '#f59e0b', label: `${skipPreCheckin} item${skipPreCheckin === 1 ? '' : 's'} awaiting check-in — click to review` };
  if (pass > 0) return { color: '#16a34a', label: `All ${pass} EHR push${pass === 1 ? '' : 'es'} succeeded` };
  return { color: '#cbd5e1', label: 'No paperwork pushed yet' };
}

const FILTER_PRESETS = {
  today: 'Today',
  tomorrow: 'Tomorrow',
  week: 'This Week',
  custom: 'Custom Range'
};

const STATUS_COLORS = {
  scheduled: { bg: 'rgba(22,163,74,0.08)', text: '#16a34a', label: 'Scheduled', border: '#16a34a' },
  'checked-in': { bg: 'rgba(6,182,212,0.08)', text: '#0891b2', label: 'Checked In', border: '#0891b2' },
  completed: { bg: 'rgba(22,163,74,0.08)', text: '#16a34a', label: 'Completed', border: '#16a34a' },
  'no-show': { bg: 'rgba(217,119,6,0.08)', text: '#d97706', label: 'No-Show', border: '#d97706' },
  canceled: { bg: 'rgba(220,38,38,0.07)', text: '#dc2626', label: 'Canceled', border: '#dc2626' },
  rescheduled: { bg: 'rgba(37,99,235,0.08)', text: '#2563eb', label: 'Rescheduled', border: '#2563eb' },
  open: { bg: 'rgba(124,58,237,0.07)', text: '#7c3aed', label: 'Open', border: '#7c3aed' }
};

function getApptStatus(appt) {
  if (!appt.patient_id && !appt.first_name && !appt.last_name) return STATUS_COLORS.open;
  return STATUS_COLORS[appt.status] || STATUS_COLORS.scheduled;
}

/**
 * Format a Date as YYYY-MM-DD in the given IANA timezone.
 * Uses Intl to get the correct local date — avoids the UTC shift bug
 * where toISOString() could return yesterday's date for eastern timezones.
 */
function formatDateInTz(d, timezone) {
  // en-CA locale gives YYYY-MM-DD format natively
  return new Intl.DateTimeFormat('en-CA', { timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
}

function getDateRange(preset, timezone = 'America/New_York') {
  const now = new Date();
  // Get today's date string in the practice timezone
  const todayStr = formatDateInTz(now, timezone);
  // Parse back to manipulate days (use noon to avoid DST edge cases)
  const todayParts = todayStr.split('-').map(Number);
  const today = new Date(todayParts[0], todayParts[1] - 1, todayParts[2], 12, 0, 0);

  const fmt = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  switch (preset) {
    case 'today':
      return { start: todayStr, end: todayStr };
    case 'tomorrow': {
      const tmrw = new Date(today);
      tmrw.setDate(tmrw.getDate() + 1);
      return { start: fmt(tmrw), end: fmt(tmrw) };
    }
    case 'week': {
      const end = new Date(today);
      end.setDate(end.getDate() + 6);
      return { start: todayStr, end: fmt(end) };
    }
    default:
      return { start: todayStr, end: todayStr };
  }
}

function Appointments({ practiceId, embedded, initialSelectedApptId }) {
  const { selectedPractice } = usePractice();
  const practiceTimezone = selectedPractice?.timezone || 'America/New_York';
  const { formatDate: fmtDate } = useFormattedDateTime(practiceTimezone);

  const formatApptDate = (dateStr) => {
    if (!dateStr) return '—';
    return fmtDate(dateStr);
  };

  const formatApptTime = (dateStr, startTime) => {
    // Prefer start_time (HH:MM) which is the actual local practice time.
    if (startTime) {
      const [hours, minutes] = startTime.split(':').map(Number);
      const period = hours >= 12 ? 'PM' : 'AM';
      const displayHour = hours % 12 || 12;
      return `${displayHour}:${String(minutes).padStart(2, '0')} ${period}`;
    }
    // appointment_date is a DATE column → serialized as UTC midnight. Formatting
    // it as a time gives a meaningless "5:00 PM PT" / "8:00 PM ET" artifact of
    // the viewer's timezone, not the real appointment time. Show "—" until the
    // next sync populates start_time properly.
    return '—';
  };
  // View mode: 'booked' (default, current behavior) shows real bookings;
  // 'open' shows the EHR-template open slots that voice AI / chat use to
  // offer availability. Staff asked for a quick lookup tab — same date
  // chips + provider/department filters work for both views, we just
  // toggle which subset of the appointments table to render.
  const [viewMode, setViewMode] = useState('booked');
  const [activeFilter, setActiveFilter] = useState('today');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [appointments, setAppointments] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedAppt, setSelectedAppt] = useState(null);
  const [apptTags, setApptTags] = useState({});
  const [intakeForms, setIntakeForms] = useState({}); // appointment_id → intake form data
  const [tagFilters, setTagFilters] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [providerFilter, setProviderFilter] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');

  const loadAppointments = useCallback(async () => {
    if (!practiceId) return;
    let range;
    if (activeFilter === 'custom') {
      if (!customStart || !customEnd) return;
      range = { start: customStart, end: customEnd };
    } else {
      range = getDateRange(activeFilter, practiceTimezone);
    }
    try {
      setLoading(true);
      setError(null);
      const res = await api.getAppointments(practiceId, {
        startDate: range.start,
        endDate: range.end,
        status: statusFilter || undefined,
        providerName: providerFilter || undefined,
        departmentName: departmentFilter || undefined
      });
      setAppointments(res.data || []);
      setSummary(res.summary || null);
      // Batch-fetch tags for loaded appointments
      if (res.data?.length > 0) {
        const ids = res.data.map(a => a.id);
        const tagRes = await api.batchGetTags(practiceId, 'appointment', ids);
        if (tagRes.success) setApptTags(tagRes.data || {});
      }
      // Fetch intake form status for all appointments.
      // GH-12: default pagination is 25 — once a practice has more intake forms than that,
      // recently-completed forms can fall off the first page and the "Intake Done" pill on
      // the matching appointment row stops rendering. Bump to 500 so the lookup covers
      // everything a practice plausibly has active without needing a dedicated endpoint.
      try {
        const intakeRes = await api.getIntakeDashboardActiveForms(practiceId, { limit: 500 });
        const forms = (intakeRes.data?.rows || intakeRes.data || []);
        const formMap = {};
        for (const f of forms) { if (f.appointment_id) formMap[f.appointment_id] = f; }
        setIntakeForms(formMap);
      } catch { /* non-blocking */ }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [practiceId, activeFilter, customStart, customEnd, statusFilter, providerFilter, departmentFilter, practiceTimezone]);

  useEffect(() => {
    loadAppointments();
    // Auto-refresh every 60 seconds to pick up status changes (check-in, cancel, etc.)
    const interval = setInterval(loadAppointments, 60000);
    return () => clearInterval(interval);
  }, [loadAppointments]);

  // GH-142: when Pre-Visit Readiness deep-links here with ?appointment_id=X, we
  // don't rely on the list-window fetch — that window defaults to "today" and
  // the appt may be days ahead. Fetch the row directly, set the appropriate
  // date range so it renders in the list, and preselect it.
  const initialAppliedRef = React.useRef(false);
  useEffect(() => {
    if (!initialSelectedApptId || !practiceId || initialAppliedRef.current) return;
    initialAppliedRef.current = true;
    (async () => {
      try {
        const res = await api.getAppointment(practiceId, initialSelectedApptId);
        const appt = res?.data;
        if (!appt) return;
        // Widen date range to include the appointment day so it's visible in the list
        const day = String(appt.appointment_date || '').slice(0, 10);
        if (day) {
          setActiveFilter('custom');
          setCustomStart(day);
          setCustomEnd(day);
        }
        setStatusFilter('');
        setSelectedAppt(appt);
      } catch (err) {
        console.warn('[Appointments] deep-link fetch failed:', err.message);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSelectedApptId, practiceId]);

  // GH-177 safety net: when a waitlist accept goes through EHRBridge, Athena
  // sometimes issues a fresh appointment_id for the rebooked slot alongside
  // the original cancelled row — the sync then creates a second row and the
  // calendar shows the same patient booked twice at the same time. Dedup
  // on (patient_id + appointment_date + start_time), keeping the row with
  // the highest numeric id (usually the freshest sync). Open slots (no
  // patient_id) are not deduped — several open slots at the same time is
  // legit for multi-provider slots.
  const dedupedAppointments = React.useMemo(() => {
    if (!appointments || appointments.length === 0) return appointments;
    const bestByKey = new Map();
    const passthrough = [];
    for (const a of appointments) {
      if (!a.patient_id) { passthrough.push(a); continue; }
      const key = `${a.patient_id}|${String(a.appointment_date || '').slice(0, 10)}|${(a.start_time || '').slice(0, 5)}`;
      const existing = bestByKey.get(key);
      if (!existing || Number(a.id || 0) > Number(existing.id || 0)) {
        bestByKey.set(key, a);
      }
    }
    return [...bestByKey.values(), ...passthrough];
  }, [appointments]);

  // Extract unique providers and departments for filter dropdowns
  const uniqueProviders = [...new Set(dedupedAppointments.map(a => a.provider_name).filter(Boolean))].sort();
  const uniqueDepartments = [...new Set(dedupedAppointments.map(a => a.department_name).filter(Boolean))].sort();

  const filteredAppointments = dedupedAppointments.filter(a => {
    // View mode: 'open' shows EHR template slots (no patient booked);
    // 'booked' (default) shows real bookings. Open-slot rows are detected
    // by patient_id IS NULL — same convention used by the voice AI
    // findOpenSlotsByDateRange query, so both surfaces stay consistent.
    const isOpenSlot = !a.patient_id;
    if (viewMode === 'open' && !isOpenSlot) return false;
    if (viewMode === 'booked' && isOpenSlot) return false;
    // Tag filter
    if (tagFilters.length > 0) {
      const aTags = apptTags[a.id] || [];
      if (!tagFilters.some(filterId => aTags.some(t => t.tag_id === filterId))) return false;
    }
    // Provider filter
    if (providerFilter && (a.provider_name || '') !== providerFilter) return false;
    // Department filter
    if (departmentFilter && (a.department_name || '') !== departmentFilter) return false;
    // Search filter (patient name, provider, appointment type, department, IDs)
    // Supports multi-word AND search: "Ayush Allen_Prime" matches provider=Ayush AND dept=Allen_Prime
    // GH-128: added patient_id / athena_patient_id / athena_appointment_id so testers
    // can jump straight to a row by entering the numeric ID shown in Patient Directory
    // or downstream integration screens.
    if (searchQuery) {
      const searchable = [
        a.first_name || a.patient_first_name || '',
        a.last_name || a.patient_last_name || '',
        a.provider_name || '',
        a.appointment_type || '',
        a.department_name || '',
        a.patient_id != null ? String(a.patient_id) : '',
        a.athena_patient_id != null ? String(a.athena_patient_id) : '',
        a.athena_appointment_id != null ? String(a.athena_appointment_id) : '',
      ].join(' ').toLowerCase();
      const terms = searchQuery.toLowerCase().split(/\s+/).filter(Boolean);
      if (!terms.every(term => searchable.includes(term))) return false;
    }
    return true;
  });

  const isCompressed = !!selectedAppt;

  if (!practiceId) {
    return <div style={styles.card}><p style={{ color: '#666' }}>Please select a practice from the dropdown above.</p></div>;
  }

  return (
    <div style={{ ...layoutStyles.container, ...(embedded ? { height: '100%' } : {}) }}>
      {/* Left Pane — Appointments List */}
      <div style={{
        ...layoutStyles.listPane,
        width: isCompressed ? '30%' : '100%',
        minWidth: isCompressed ? '280px' : undefined,
      }}>
        <div style={{ padding: isCompressed ? '12px' : '24px 32px' }}>
          <div style={styles.header}>
            <h2 style={{ ...styles.title, fontSize: isCompressed ? '16px' : 24 }}>
              Appointments {isCompressed ? `(${filteredAppointments.length})` : ''}
            </h2>
            {!isCompressed && (
              <button onClick={loadAppointments} style={styles.refreshBtn} disabled={loading}>
                {loading ? 'Loading...' : 'Refresh'}
              </button>
            )}
          </div>

          {/* View tabs (Booked vs Open Slots) — staff use Open Slots for
              quick availability lookup. Voice AI / Chat read the same
              underlying rows via Appointment.findOpenSlotsByDateRange. */}
          {!isCompressed && (
            <div style={styles.viewTabs}>
              <button
                onClick={() => setViewMode('booked')}
                style={{ ...styles.viewTab, ...(viewMode === 'booked' ? styles.viewTabActive : {}) }}
              >
                📅 Booked Appointments
              </button>
              <button
                onClick={() => setViewMode('open')}
                style={{ ...styles.viewTab, ...(viewMode === 'open' ? styles.viewTabActive : {}) }}
              >
                🕓 Open Slots
              </button>
            </div>
          )}

          {/* Filter Tabs — hide when compressed */}
          {!isCompressed && (
            <div style={styles.filterBar}>
              <div style={styles.filterTabs}>
                {Object.entries(FILTER_PRESETS).map(([key, label]) => (
                  <button
                    key={key}
                    onClick={() => setActiveFilter(key)}
                    style={{
                      ...styles.filterTab,
                      ...(activeFilter === key ? styles.filterTabActive : {})
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {activeFilter === 'custom' && (
                <div style={styles.customDates}>
                  <label style={styles.dateLabel}>
                    From
                    <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)} style={styles.dateInput} />
                  </label>
                  <label style={styles.dateLabel}>
                    To
                    <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)} style={styles.dateInput} />
                  </label>
                </div>
              )}

              <div style={styles.statusFilter}>
                <input
                  type="text"
                  placeholder="Search patient, provider, type..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  style={{ ...styles.select, width: 220 }}
                />
                <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={styles.select}>
                  <option value="">All Statuses</option>
                  <option value="scheduled">Scheduled</option>
                  <option value="checked-in">Checked In</option>
                  <option value="completed">Completed</option>
                  <option value="no-show">No-Show</option>
                  <option value="canceled">Canceled</option>
                  <option value="rescheduled">Rescheduled</option>
                </select>
                {uniqueProviders.length > 1 && (
                  <select value={providerFilter} onChange={e => setProviderFilter(e.target.value)} style={styles.select}>
                    <option value="">All Providers</option>
                    {uniqueProviders.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                )}
                {uniqueDepartments.length > 1 && (
                  <select value={departmentFilter} onChange={e => setDepartmentFilter(e.target.value)} style={styles.select}>
                    <option value="">All Departments</option>
                    {uniqueDepartments.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                )}
              </div>
              <TagFilterDropdown
                practiceId={practiceId}
                entityType="appointment"
                selected={tagFilters}
                onChange={setTagFilters}
              />
              {(searchQuery || statusFilter || providerFilter || departmentFilter || tagFilters.length > 0) && (
                <button
                  onClick={() => { setSearchQuery(''); setStatusFilter(''); setProviderFilter(''); setDepartmentFilter(''); setTagFilters([]); }}
                  style={styles.clearBtn}
                >
                  Clear Filters
                </button>
              )}
            </div>
          )}

          {/* Summary Cards — Booked view shows the lifecycle status counts.
              Open Slots view shows availability counts (the booked-status
              cards are meaningless for empty slots). */}
          {!isCompressed && summary && viewMode === 'open' && (
            <div style={styles.summaryRow}>
              <div style={{ ...styles.summaryCard, outline: '2px solid #7c3aed' }} title="Open slots in selected range">
                <div style={{ ...styles.summaryValue, color: '#7c3aed' }}>{filteredAppointments.length}</div>
                <div style={styles.summaryLabel}>Open Slots</div>
              </div>
              <div style={styles.summaryCard} title="Distinct providers with open slots">
                <div style={{ ...styles.summaryValue, color: '#0f172a' }}>
                  {new Set(filteredAppointments.map(a => a.provider_name).filter(Boolean)).size}
                </div>
                <div style={styles.summaryLabel}>Providers</div>
              </div>
              <div style={styles.summaryCard} title="Distinct departments with open slots">
                <div style={{ ...styles.summaryValue, color: '#0f172a' }}>
                  {new Set(filteredAppointments.map(a => a.department_name).filter(Boolean)).size}
                </div>
                <div style={styles.summaryLabel}>Departments</div>
              </div>
            </div>
          )}
          {!isCompressed && summary && viewMode === 'booked' && (
            <div style={styles.summaryRow}>
              <div
                style={{ ...styles.summaryCard, cursor: 'pointer', outline: statusFilter === '' ? '2px solid #0f172a' : 'none' }}
                onClick={() => setStatusFilter('')}
                title="Show all"
              >
                <div style={{ ...styles.summaryValue, color: '#0f172a' }}>{summary.total}</div>
                <div style={styles.summaryLabel}>Total</div>
              </div>
              <div
                style={{ ...styles.summaryCard, cursor: 'pointer', outline: statusFilter === 'scheduled' ? '2px solid #16a34a' : 'none' }}
                onClick={() => setStatusFilter(statusFilter === 'scheduled' ? '' : 'scheduled')}
                title="Filter to Scheduled"
              >
                <div style={{ ...styles.summaryValue, color: '#16a34a' }}>{summary.scheduled}</div>
                <div style={styles.summaryLabel}>Scheduled</div>
              </div>
              <div
                style={{ ...styles.summaryCard, cursor: 'pointer', outline: statusFilter === 'checked-in' ? '2px solid #0891b2' : 'none' }}
                onClick={() => setStatusFilter(statusFilter === 'checked-in' ? '' : 'checked-in')}
                title="Filter to Checked In"
              >
                <div style={{ ...styles.summaryValue, color: '#0891b2' }}>{summary['checked-in'] || 0}</div>
                <div style={styles.summaryLabel}>Checked In</div>
              </div>
              <div
                style={{ ...styles.summaryCard, cursor: 'pointer', outline: statusFilter === 'completed' ? '2px solid #16a34a' : 'none' }}
                onClick={() => setStatusFilter(statusFilter === 'completed' ? '' : 'completed')}
                title="Filter to Completed"
              >
                <div style={{ ...styles.summaryValue, color: '#16a34a' }}>{summary.completed}</div>
                <div style={styles.summaryLabel}>Completed</div>
              </div>
              <div
                style={{ ...styles.summaryCard, cursor: 'pointer', outline: statusFilter === 'no-show' ? '2px solid #d97706' : 'none' }}
                onClick={() => setStatusFilter(statusFilter === 'no-show' ? '' : 'no-show')}
                title="Filter to No-Show"
              >
                <div style={{ ...styles.summaryValue, color: '#d97706' }}>{summary['no-show']}</div>
                <div style={styles.summaryLabel}>No-Show</div>
              </div>
              <div
                style={{ ...styles.summaryCard, cursor: 'pointer', outline: statusFilter === 'canceled' ? '2px solid #dc2626' : 'none' }}
                onClick={() => setStatusFilter(statusFilter === 'canceled' ? '' : 'canceled')}
                title="Filter to Canceled"
              >
                <div style={{ ...styles.summaryValue, color: '#dc2626' }}>{summary.canceled}</div>
                <div style={styles.summaryLabel}>Canceled</div>
              </div>
            </div>
          )}

          {error && <div style={styles.error}>Error: {error}</div>}
        </div>

        {/* Appointment List */}
        {isCompressed ? (
          /* Compressed mode — compact clickable rows */
          <div style={layoutStyles.compactList}>
            {loading ? (
              <div style={{ padding: 24, textAlign: 'center', color: '#6b7280' }}>Loading...</div>
            ) : filteredAppointments.length === 0 ? (
              <div style={{ padding: 24, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
                No appointments found.
              </div>
            ) : (
              filteredAppointments.map(appt => {
                const statusStyle = getApptStatus(appt);
                const patientName = appt.first_name || appt.last_name
                  ? `${appt.first_name || ''} ${appt.last_name || ''}`.trim()
                  : (appt.patient_id ? `#${appt.patient_id}` : 'Open Slot');
                const initials = appt.first_name && appt.last_name
                  ? `${appt.first_name[0]}${appt.last_name[0]}`.toUpperCase()
                  : (appt.patient_id ? '#' : 'OS');
                const isSelected = selectedAppt?.id === appt.id;
                return (
                  <div
                    key={appt.id}
                    onClick={() => setSelectedAppt(appt)}
                    style={{
                      ...layoutStyles.compactRow,
                      background: isSelected ? 'rgba(37,99,235,0.08)' : 'transparent',
                      borderLeftColor: isSelected ? '#2563eb' : (statusStyle.border || 'transparent'),
                      opacity: appt.status === 'canceled' ? 0.7 : 1,
                    }}
                  >
                    <div style={{
                      ...layoutStyles.compactAvatar,
                      background: statusStyle.bg,
                      color: statusStyle.text,
                    }}>
                      {initials}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={layoutStyles.compactName}>{patientName}</div>
                      <div style={layoutStyles.compactMeta}>
                        {formatApptTime(appt.appointment_date, appt.start_time)}
                        {appt.appointment_type && ` · ${appt.appointment_type}`}
                      </div>
                    </div>
                    <span style={{
                      ...layoutStyles.compactBadge,
                      background: statusStyle.bg,
                      color: statusStyle.text
                    }}>
                      {statusStyle.label}
                    </span>
                  </div>
                );
              })
            )}
          </div>
        ) : (
          /* Full mode — original table */
          <div style={{ padding: '0 32px 24px' }}>
            <div style={styles.card}>
              {loading ? (
                <div style={styles.loadingText}>Loading appointments...</div>
              ) : filteredAppointments.length === 0 ? (
                <div style={styles.empty}>No appointments found for the selected date range.</div>
              ) : (
                <div style={styles.tableWrapper}>
                  <AppointmentsTable
                    practiceId={practiceId}
                    appointments={filteredAppointments}
                    apptTags={apptTags}
                    intakeForms={intakeForms}
                    viewMode={viewMode}
                    setSelectedAppt={setSelectedAppt}
                    formatApptDate={formatApptDate}
                    formatApptTime={formatApptTime}
                    getApptStatus={getApptStatus}
                    styles={styles}
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Right Pane — Detail Panel */}
      {selectedAppt && (
        <div style={layoutStyles.detailPane}>
          <AppointmentDetailPanel
            appointment={selectedAppt}
            practiceId={practiceId}
            onClose={() => setSelectedAppt(null)}
            onAppointmentUpdated={() => loadAppointments()}
          />
        </div>
      )}
    </div>
  );
}

function AppointmentsTable({ practiceId, appointments, apptTags, intakeForms, viewMode, setSelectedAppt, formatApptDate, formatApptTime, getApptStatus, styles }) {
  const navigate = useNavigate();
  const sortableRows = appointments.map(a => {
    const intake = intakeForms[a.id];
    // 2026-06-10 fix: combine appointment_date (date-only at midnight UTC) with
    // start_time (time-of-day "HH:MM:SS") so the Date and Time columns sort by
    // the actual chronological order — both asc and desc directions. Prior key
    // was date-only, so within a single day all rows tied and click-to-sort
    // (especially descending) appeared broken because the order was disk-order.
    const apptMs = (() => {
      if (!a.appointment_date) return null;
      let ms = new Date(a.appointment_date).getTime();
      if (a.start_time) {
        const [hh = 0, mm = 0, ss = 0] = a.start_time.split(':').map(Number);
        ms += ((hh || 0) * 3600 + (mm || 0) * 60 + (ss || 0)) * 1000;
      }
      return ms;
    })();
    return {
      ...a,
      _appt_ms: apptMs,
      _patient_name: `${a.first_name || a.patient_first_name || ''} ${a.last_name || a.patient_last_name || ''}`.trim().toLowerCase(),
      _intake_status: intake?.status || '',
      _completion_pct: intake?.completion_pct != null ? Number(intake.completion_pct) : null,
      _current_section: intake?.current_section || '',
    };
  });
  const { sortedRows, sortKey, sortDir, handleSort } = useTableSort(sortableRows);
  const sortTh = (label, key) => (
    <th style={{ ...styles.th, cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort(key)}>
      {label}{sortArrow(key, sortKey, sortDir)}
    </th>
  );
  return (
    <table style={styles.table}>
      <thead>
        <tr>
          {sortTh('Date', '_appt_ms')}
          {sortTh('Time', '_appt_ms')}
          {viewMode === 'booked' && sortTh('Patient', '_patient_name')}
          {viewMode === 'booked' && <th style={styles.th}>Status</th>}
          {sortTh('Type', 'appointment_type')}
          {sortTh('Provider', 'provider_name')}
          {sortTh('Department', 'department_name')}
          {sortTh('Duration', 'duration_minutes')}
          {viewMode === 'booked' && <th style={styles.th}>Tags</th>}
          {viewMode === 'booked' && sortTh('Status', 'status')}
          {viewMode === 'booked' && sortTh('Intake', '_intake_status')}
          {viewMode === 'booked' && sortTh('Completion', '_completion_pct')}
          {viewMode === 'booked' && sortTh('Section', '_current_section')}
        </tr>
      </thead>
      <tbody>
        {sortedRows.map(appt => {
                        const statusStyle = getApptStatus(appt);
                        const patientName = appt.first_name || appt.last_name
                          ? `${appt.first_name || ''} ${appt.last_name || ''}`.trim()
                          : (appt.patient_id ? `Patient #${appt.patient_id}` : '— Open Slot —');
                        return (
                          <tr
                            key={appt.id}
                            onClick={() => setSelectedAppt(appt)}
                            style={{
                              ...styles.tr,
                              cursor: 'pointer',
                              backgroundColor: appt.status === 'canceled' ? '#fef2f2' : appt.status === 'no-show' ? '#fffbeb' : 'transparent',
                              opacity: appt.status === 'canceled' ? 0.7 : 1,
                            }}
                          >
                            <td style={styles.td}>{formatApptDate(appt.appointment_date)}</td>
                            <td style={styles.td}>{formatApptTime(appt.appointment_date, appt.start_time)}</td>
                            {viewMode === 'booked' && (
                              <td style={styles.td}>
                                <div style={{ fontWeight: appt.patient_id ? 500 : 400, color: appt.patient_id ? '#111' : '#999', fontStyle: appt.patient_id ? 'normal' : 'italic' }}>
                                  {patientName}
                                </div>
                                {appt.patient_phone && <div style={styles.subText}>{appt.patient_phone}</div>}
                              </td>
                            )}
                            {viewMode === 'booked' && (
                              <td style={styles.td} onClick={(e) => e.stopPropagation()}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  {intakeForms[appt.id] && (() => {
                                    const bell = bellState(appt);
                                    const intake = intakeForms[appt.id];
                                    // GH-141 (2026-06-20 follow-up): bell click is action-oriented but
                                    // the action varies by current push state:
                                    //   green (all succeeded)  → no-op success alert (clicking should
                                    //                            not error with "already completed")
                                    //   red   (any failed)     → call retry endpoint (not the push
                                    //                            endpoint which 400s on completed/partial)
                                    //   yellow/gray            → fresh push
                                    // Shift+click always navigates to Intake Center.
                                    const allSucceeded = (appt.push_total || 0) > 0
                                      && (appt.push_fail || 0) === 0
                                      && (appt.push_pass || 0) === (appt.push_total || 0);
                                    const hasFailures = (appt.push_fail || 0) > 0;
                                    const ctaLabel = allSucceeded
                                      ? 'view summary'
                                      : (hasFailures ? 'retry failed pushes' : 'push to EHR');
                                    const onBellClick = async (e) => {
                                      if (e.shiftKey) {
                                        navigate(`/practice-view/care-hub?tab=intake&appointment_id=${appt.id}`);
                                        return;
                                      }
                                      if (allSucceeded) {
                                        alert(`All ${appt.push_pass} EHR push${appt.push_pass === 1 ? '' : 'es'} already succeeded for this appointment.\n\nShift+click the bell to view the per-call breakdown in Intake Center.`);
                                        return;
                                      }
                                      if (intake.status !== 'COMPLETED') {
                                        alert(`Cannot push to EHR — intake form is ${intake.status || 'not started'}. Patient needs to complete it first.\n\nShift+click the bell to view details.`);
                                        return;
                                      }
                                      const action = hasFailures ? 'Retry failed EHR pushes' : 'Push intake data to EHR';
                                      if (!window.confirm(`${action} now? This sends every field the patient has completed.`)) return;
                                      try {
                                        const url = hasFailures
                                          ? (intake.generation_log_id
                                              ? `/practices/${practiceId}/intake/generations/${intake.generation_log_id}/retry-ehr-push`
                                              : `/practices/${practiceId}/intake/${appt.id}/retry-ehr-push`)
                                          : (intake.generation_log_id
                                              ? `/practices/${practiceId}/intake/generations/${intake.generation_log_id}/push-to-ehr`
                                              : `/practices/${practiceId}/intake/${appt.id}/push-to-ehr`);
                                        const result = await api.post(url);
                                        const summary = result?.data?.summary;
                                        const status = result?.data?.ehr_push_status || 'completed';
                                        alert(`Push ${status}: ${summary?.pass || 0} ok · ${summary?.fail || 0} failed · ${summary?.skip || 0} skipped`);
                                      } catch (err) {
                                        // Friendly translation of the "already completed" 400 so it doesn't
                                        // look like a real failure when staff click on green-bell rows.
                                        const msg = String(err?.message || 'Unknown error');
                                        if (/already completed/i.test(msg)) {
                                          alert('All EHR pushes for this appointment already completed. Shift+click the bell to view details.');
                                        } else {
                                          alert(`Push failed: ${msg}`);
                                        }
                                      }
                                    };
                                    return (
                                      <button
                                        type="button"
                                        title={`${bell.label}\nClick: ${ctaLabel} · Shift+Click: view details`}
                                        aria-label={`EHR push status: ${bell.label}. Click to ${ctaLabel}, shift-click to view details.`}
                                        onClick={onBellClick}
                                        style={{
                                          background: 'transparent', border: 'none', padding: 4, cursor: 'pointer',
                                          color: bell.color, fontSize: 18, lineHeight: 1,
                                        }}
                                      >
                                        🔔
                                      </button>
                                    );
                                  })()}
                                  {appt.pending_icr_count > 0 && (
                                    <button
                                      type="button"
                                      title={`${appt.pending_icr_count} insurance change request${appt.pending_icr_count === 1 ? '' : 's'} pending review`}
                                      aria-label={`${appt.pending_icr_count} insurance change requests need review`}
                                      onClick={() => navigate(`/practice-view/insurance-change-requests?focus=${appt.pending_icr_id}`)}
                                      style={{
                                        background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 6,
                                        padding: '2px 6px', cursor: 'pointer',
                                        color: '#92400e', fontSize: 11, fontWeight: 600,
                                        display: 'flex', alignItems: 'center', gap: 4,
                                      }}
                                    >
                                      💳 ICR
                                    </button>
                                  )}
                                </div>
                              </td>
                            )}
                            <td style={styles.td}>{appt.appointment_type || '—'}</td>
                            <td style={styles.td}>{appt.provider_name || '—'}</td>
                            <td style={styles.td}>{appt.department_name || '—'}</td>
                            <td style={styles.td}>{appt.duration_minutes ? `${appt.duration_minutes} min` : '—'}</td>
                            {viewMode === 'booked' && (
                              <td style={styles.td}>
                                <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                                  {(apptTags[appt.id] || []).map(t => (
                                    <TagBadge key={t.tag_id} name={t.name} color={t.color} />
                                  ))}
                                </div>
                              </td>
                            )}
                            {viewMode === 'booked' && (
                              <>
                                <td style={styles.td}>
                                  <span style={{
                                    display: 'inline-block', padding: '2px 10px', borderRadius: 12,
                                    fontSize: 12, fontWeight: 600,
                                    backgroundColor: statusStyle.bg, color: statusStyle.text
                                  }}>
                                    {statusStyle.label}
                                  </span>
                                  {/* GH-12 (v2): `appointments.status` tracks the Athena encounter lifecycle
                                      (Scheduled → Checked-In → Completed) and shouldn't flip on intake submit —
                                      the encounter isn't complete until the provider signs off. First iteration
                                      used an inline "· 📋✓" emoji next to the appointment status; reporter
                                      missed it. Replaced with a distinct green "Intake Done" pill rendered on
                                      a new line so both truths read at a glance. */}
                                  {intakeForms[appt.id]?.status === 'COMPLETED' && (
                                    <div
                                      title={`Intake submitted${intakeForms[appt.id]?.completed_at ? ' ' + new Date(intakeForms[appt.id].completed_at).toLocaleString() : ''}`}
                                      style={{
                                        display: 'inline-block', marginTop: 4, padding: '2px 8px',
                                        borderRadius: 10, fontSize: 11, fontWeight: 600,
                                        backgroundColor: '#dcfce7', color: '#166534',
                                        border: '1px solid #86efac'
                                      }}
                                    >
                                      ✓ Intake Done
                                    </div>
                                  )}
                                </td>
                                {(() => {
                                  const intake = intakeForms[appt.id];
                                  if (!intake) return (<><td style={styles.td}><span style={{ color: '#9ca3af', fontSize: 12 }}>—</span></td><td style={styles.td}>—</td><td style={styles.td}>—</td></>);
                                  const INTAKE_STATUS_COLORS = { NOT_STARTED: { bg: '#f3f4f6', text: '#6b7280' }, OPENED: { bg: '#dbeafe', text: '#1e40af' }, IN_PROGRESS: { bg: '#fef3c7', text: '#92400e' }, COMPLETED: { bg: '#dcfce7', text: '#166534' } };
                                  const sc = INTAKE_STATUS_COLORS[intake.status] || INTAKE_STATUS_COLORS.NOT_STARTED;
                                  const pct = Number(intake.completion_pct) || 0;
                                  const lastAct = intake.last_activity_at ? (() => { const mins = Math.round((Date.now() - new Date(intake.last_activity_at).getTime()) / 60000); if (mins < 60) return `${mins}m ago`; if (mins < 1440) return `${Math.round(mins/60)}h ago`; return `${Math.round(mins/1440)}d ago`; })() : '—';
                                  return (<>
                                    <td style={styles.td}><span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, backgroundColor: sc.bg, color: sc.text }}>{intake.status?.replace('_', ' ') || '—'}</span></td>
                                    <td style={styles.td}><div style={{ display: 'flex', alignItems: 'center', gap: 6 }}><div style={{ width: 50, height: 6, backgroundColor: '#e5e7eb', borderRadius: 3, overflow: 'hidden' }}><div style={{ width: `${pct}%`, height: '100%', backgroundColor: pct >= 100 ? '#22c55e' : '#3b82f6', borderRadius: 3 }} /></div><span style={{ fontSize: 12, color: '#4b5563' }}>{pct.toFixed(0)}%</span></div></td>
                                    <td style={styles.td}><span style={{ fontSize: 12, color: intake.status === 'COMPLETED' ? '#166534' : '#6b7280' }}>{intake.status === 'COMPLETED' ? 'Completed' : (intake.current_section || '—')}</span><div style={{ fontSize: 10, color: '#9ca3af' }}>{lastAct}</div></td>
                                  </>);
                                })()}
                              </>
                            )}
                          </tr>
                        );
        })}
      </tbody>
    </table>
  );
}

const layoutStyles = {
  container: {
    display: 'flex',
    height: 'calc(100vh - 64px)',
    overflow: 'hidden',
    background: '#f8fafc',
  },
  listPane: {
    transition: 'width 0.3s ease',
    overflowY: 'auto',
    overflowX: 'hidden',
    flexShrink: 0,
    background: '#ffffff',
    borderRight: '1px solid #e2e8f0',
  },
  detailPane: {
    width: '70%',
    flexShrink: 0,
    borderLeft: '1px solid #e2e8f0',
    background: '#f8fafc',
    overflowY: 'auto',
  },
  compactList: {
    overflow: 'hidden',
    padding: '0 12px 12px',
  },
  compactRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '11px 14px',
    cursor: 'pointer',
    borderRadius: '10px',
    borderLeft: '3px solid transparent',
    transition: 'all 0.2s ease',
    marginBottom: '3px',
  },
  compactAvatar: {
    width: '36px',
    height: '36px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '11px',
    fontWeight: '700',
    flexShrink: 0,
    fontFamily: "'JetBrains Mono', monospace",
  },
  compactName: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#0f172a',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  compactMeta: {
    fontSize: '11px',
    color: '#94a3b8',
    marginTop: '2px',
  },
  compactBadge: {
    padding: '3px 10px',
    borderRadius: 20,
    fontSize: 10,
    fontWeight: 700,
    flexShrink: 0,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
};

const styles = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 },
  title: { margin: 0, fontWeight: 700, color: '#0f172a', fontFamily: "'JetBrains Mono', monospace", letterSpacing: '-0.5px' },
  refreshBtn: {
    padding: '8px 16px', backgroundColor: '#2563eb', color: '#fff', border: 'none',
    borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600,
    transition: 'all 0.2s',
  },
  filterBar: {
    display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20, flexWrap: 'wrap'
  },
  filterTabs: { display: 'flex', gap: 4, backgroundColor: '#f1f5f9', borderRadius: 10, padding: 4 },
  filterTab: {
    padding: '8px 16px', border: 'none', borderRadius: 8, cursor: 'pointer',
    fontSize: 13, fontWeight: 600, backgroundColor: 'transparent', color: '#94a3b8',
    transition: 'all 0.2s'
  },
  filterTabActive: { backgroundColor: '#fff', color: '#0f172a', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' },
  viewTabs: {
    display: 'flex', gap: 8, marginBottom: 16, borderBottom: '1px solid #e2e8f0',
  },
  viewTab: {
    padding: '10px 18px', border: 'none', background: 'transparent', cursor: 'pointer',
    fontSize: 14, fontWeight: 600, color: '#64748b',
    borderBottom: '2px solid transparent', marginBottom: -1,
    transition: 'all 0.2s',
  },
  viewTabActive: {
    color: '#2563eb', borderBottom: '2px solid #2563eb',
  },
  customDates: { display: 'flex', gap: 12, alignItems: 'center' },
  dateLabel: { fontSize: 13, color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 6 },
  dateInput: { padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13 },
  statusFilter: {},
  select: { padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, color: '#334155', backgroundColor: '#fff' },
  summaryRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: 8, marginBottom: 20 },
  summaryCard: {
    padding: '10px 12px', backgroundColor: '#f8fafc', borderRadius: 10,
    border: '1px solid #e2e8f0', textAlign: 'center',
    transition: 'border-color 0.2s',
  },
  summaryValue: { fontSize: 22, fontWeight: 700, color: '#0f172a', fontFamily: "'JetBrains Mono', monospace" },
  summaryLabel: { fontSize: 10, color: '#94a3b8', marginTop: 2, textTransform: 'uppercase', letterSpacing: 1, fontWeight: 600 },
  card: { backgroundColor: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' },
  tableWrapper: { overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: {
    padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid #e2e8f0',
    fontSize: 10, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1.2,
    whiteSpace: 'nowrap', backgroundColor: '#f8fafc'
  },
  tr: { borderBottom: '1px solid #f1f5f9' },
  td: { padding: '12px 16px', color: '#334155', whiteSpace: 'nowrap' },
  subText: { fontSize: 11, color: '#94a3b8', marginTop: 2 },
  error: { padding: '12px 16px', backgroundColor: '#fef2f2', color: '#991b1b', borderRadius: 10, marginBottom: 16, fontSize: 13 },
  loadingText: { padding: 40, textAlign: 'center', color: '#94a3b8' },
  empty: { padding: 40, textAlign: 'center', color: '#94a3b8', fontSize: 14 },
  clearBtn: {
    padding: '6px 14px', backgroundColor: 'rgba(220,38,38,0.07)', color: '#dc2626', border: '1px solid rgba(220,38,38,0.15)',
    borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600
  }
};

export default Appointments;
